import { useMemo, useState } from "react";
import { Badge, Button, Card, EmptyState, Field, Input, Modal } from "./ui.js";
import { formatCurrency } from "../lib/format.js";
import type { Item } from "../types.js";

function num(value: string | null): number {
  if (value === null) return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function itemLine(item: Item, index: number): string {
  const details = [item.series, item.boxNo ? `#${item.boxNo}` : ""].filter(Boolean).join(" ");
  const suffix = details ? ` — ${details}` : "";
  return `${index + 1}. ${item.name}${suffix} — ${formatCurrency(item.currentValue)}`;
}

/**
 * Builds a paste-ready block for a Whatnot listing. Everything here is derived
 * in the browser; no data is written.
 */
function buildCard(items: Item[], bidPercent: number): string {
  const totalValue = items.reduce((sum, item) => sum + num(item.currentValue), 0);
  const totalCost = items.reduce((sum, item) => sum + num(item.purchasePrice), 0);
  const startingBid = totalValue * (bidPercent / 100);
  const divider = "-".repeat(34);

  return [
    `${items.length}-Item Bundle`,
    divider,
    ...items.map(itemLine),
    divider,
    `Items: ${items.length}`,
    `Total value: ${formatCurrency(totalValue.toFixed(2))}`,
    `Total cost: ${formatCurrency(totalCost.toFixed(2))}`,
    `Starting bid: ${formatCurrency(startingBid.toFixed(2))} (${bidPercent}% of value)`,
  ].join("\n");
}

export function BundleDialog({ items, onClose }: { items: Item[]; onClose: () => void }) {
  const [bundle, setBundle] = useState<Item[]>(items);
  const [bidPercent, setBidPercent] = useState(60);
  const [copied, setCopied] = useState(false);

  const totals = useMemo(() => {
    const value = bundle.reduce((sum, item) => sum + num(item.currentValue), 0);
    const cost = bundle.reduce((sum, item) => sum + num(item.purchasePrice), 0);
    return { value, cost, profit: value - cost };
  }, [bundle]);

  const card = useMemo(
    () => (bundle.length === 0 ? "" : buildCard(bundle, bidPercent)),
    [bundle, bidPercent],
  );

  async function copyCard() {
    try {
      await navigator.clipboard.writeText(card);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Modal title={`Bundle builder (${bundle.length} item${bundle.length === 1 ? "" : "s"})`} onClose={onClose} wide>
      <div className="space-y-4">
        <p className="text-sm text-slate-400">
          A scratchpad for a single order. Totals and the copy-paste card are calculated here and
          nothing is saved to your inventory.
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <Card>
            <div className="text-xs uppercase tracking-wide text-slate-400">Total cost</div>
            <div className="text-lg font-semibold text-slate-100">
              {formatCurrency(totals.cost.toFixed(2))}
            </div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-wide text-slate-400">Total value</div>
            <div className="text-lg font-semibold text-slate-100">
              {formatCurrency(totals.value.toFixed(2))}
            </div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-wide text-slate-400">Potential margin</div>
            <div className="text-lg font-semibold text-emerald-300">
              {formatCurrency(totals.profit.toFixed(2))}
            </div>
          </Card>
        </div>

        {bundle.length === 0 ? (
          <EmptyState
            title="No items in this bundle"
            hint="Tick items in the inventory list and choose Bundle to add them."
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-slate-800">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Item</th>
                  <th className="px-3 py-2 text-right font-medium">Cost</th>
                  <th className="px-3 py-2 text-right font-medium">Value</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {bundle.map((item) => (
                  <tr key={item.id}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-100">{item.name}</div>
                      <div className="text-xs text-slate-400">
                        {[item.series, item.boxNo ? `#${item.boxNo}` : ""]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right text-slate-300">
                      {formatCurrency(item.purchasePrice)}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-200">
                      {formatCurrency(item.currentValue)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="ghost"
                        onClick={() => setBundle(bundle.filter((entry) => entry.id !== item.id))}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="grid items-end gap-3 sm:grid-cols-[200px_1fr]">
          <Field label="Starting bid (%)" hint="Percentage of total value">
            <Input
              type="number"
              min={0}
              max={200}
              value={bidPercent}
              onChange={(event) => setBidPercent(Number(event.target.value) || 0)}
            />
          </Field>
          <div className="flex justify-end">
            <Button onClick={copyCard} disabled={bundle.length === 0}>
              {copied ? "Copied!" : "Copy bundle card"}
            </Button>
          </div>
        </div>

        <div>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-slate-400">Card preview</span>
            {copied ? <Badge tone="green">Copied to clipboard</Badge> : null}
          </div>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-slate-800 bg-slate-900/60 p-3 text-xs text-slate-200">
            {card || "Nothing selected yet."}
          </pre>
        </div>

        <div className="flex justify-end border-t border-slate-800 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
